import { useEffect, useMemo, useState } from 'react'
import { Empty } from 'antd'
import { ChangeOwnerModalProps } from './types'
import { Button, EmptyState, ListItem, ListWrapper, Name, Spin, StyledModal } from './styles'
import CustomRadio from '@/components/common/CustomRadio'
import message from '@/libs/message'
import { useHealthClaimAutoAssignment } from '@/services/healthClaim/useHealthClaimAutoAssignment'
import { useChangeClaimOwner } from '@/services/healthClaim/useChangeClaimOwner'
import { useQueryClient } from '@tanstack/react-query'

const ChangeOwnerModal = ({
  isModalOpen,
  claimFormId,
  currentOwnerId,
  onClose,
}: ChangeOwnerModalProps) => {
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const queryMembers = useHealthClaimAutoAssignment({
    search: '',
    page: 1,
    limit: 100,
    enabled: isModalOpen,
  })
  const mutation = useChangeClaimOwner()

  useEffect(() => {
    if (isModalOpen) {
      setSelectedOwnerId(currentOwnerId ?? null)
    }
  }, [currentOwnerId, isModalOpen])

  const memberOptions = useMemo(() => queryMembers.data?.data ?? [], [queryMembers.data])

  const handleConfirm = () => {
    if (!claimFormId || !selectedOwnerId) {
      return
    }

    mutation.mutate(
      { claimFormId, backofficeUserId: selectedOwnerId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['useClaimList'] })
          queryClient.invalidateQueries({ queryKey: ['useSarabanList'] })
          queryClient.invalidateQueries({ queryKey: ['useGetClaimDetail', claimFormId] })
          message.success('แก้ไขสำเร็จ')
          onClose()
        },
        onError: () => {
          message.error('ไม่สามารถเปลี่ยนผู้ดำเนินการได้ กรุณาลองใหม่อีกครั้ง')
        },
      },
    )
  }

  const isConfirmDisabled =
    !selectedOwnerId || selectedOwnerId === currentOwnerId || mutation.isPending

  return (
    <StyledModal
      open={isModalOpen}
      closable={false}
      centered
      width={420}
      title="เปลี่ยนผู้ดำเนินการพิจารณาเคลมในรายการนี้"
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          ยกเลิก
        </Button>,
        <Button key="confirm" type="primary" disabled={isConfirmDisabled} onClick={handleConfirm}>
          ยืนยัน
        </Button>,
      ]}
    >
      {queryMembers.isLoading ? (
        <Spin />
      ) : memberOptions.length === 0 ? (
        <EmptyState>
          <Empty description="ไม่พบรายชื่อ" />
        </EmptyState>
      ) : (
        <ListWrapper>
          {memberOptions.map((member) => {
            const isSelected = selectedOwnerId === member.backofficeUserId

            return (
              <ListItem
                key={member.backofficeUserId}
                selected={isSelected}
                onClick={() => setSelectedOwnerId(member.backofficeUserId)}
              >
                <Name>{`${member.firstname} ${member.lastname}`}</Name>
                <CustomRadio checked={isSelected} />
              </ListItem>
            )
          })}
        </ListWrapper>
      )}
    </StyledModal>
  )
}

export default ChangeOwnerModal
